'use client'
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'

export type AudioType = 'chapter' | 'hotspots' | 'silence' | 'intro' | 'outro'

interface AudioState {
  audioIsReady: boolean
  currentAudio: string | null
  currentPosition: number
  previousAudio: string | null
  previousPosition: number
  isPlaying: boolean
  isMuted: boolean
  volume: number
  audioType: AudioType | null
  hasInteracted: boolean
}

interface AudioContextType extends AudioState {
  play: () => void
  pause: () => void
  setVolume: (volume: number) => void
  setMuted: (muted: boolean) => void
  toggleMute: () => void
  seek: (position: number) => void
  setCurrentAudio: (src: string | null, position?: number, audioType?: AudioType | null, triggerPlay?: boolean) => void
  resumePreviousAudio: () => void
  // New functions to be called from AudioElement
  setAudioReady: (isReady: boolean) => void
  setHasInteracted: (interacted: boolean) => void
  setAudioPosition: (position: number) => void
  setAudioType: (audioType: AudioType | null) => void
}

const initialState: AudioState = {
  audioIsReady: false,
  currentAudio: null,
  currentPosition: 0,
  previousAudio: null,
  previousPosition: 0,
  isPlaying: false,
  isMuted: false,
  volume: 1.0,
  audioType: null,
  hasInteracted: false,
}

const AudioContext = createContext<AudioContextType | undefined>(undefined)

export function AudioProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AudioState>(initialState)

  const play = useCallback(() => setState((prev) => ({ ...prev, isPlaying: true })), [])
  const pause = useCallback(() => setState((prev) => ({ ...prev, isPlaying: false })), [])
  const setVolume = useCallback((volume: number) => setState((prev) => ({ ...prev, volume })), [])
  const setMuted = useCallback((muted: boolean) => setState((prev) => ({ ...prev, isMuted: muted })), [])
  const toggleMute = useCallback(() => setState((prev) => ({ ...prev, isMuted: !prev.isMuted })), [])
  const seek = useCallback((position: number) => setState((prev) => ({ ...prev, currentPosition: position })), [])

  const setAudioReady = useCallback((isReady: boolean) => {
    setState((prev) => ({ ...prev, audioIsReady: isReady }))
  }, [])

  const setHasInteracted = useCallback((hasInteracted: boolean) => setState((prev) => ({ ...prev, hasInteracted })), [])

  const setAudioPosition = useCallback((position: number) => {
    setState((prev) => ({ ...prev, currentPosition: position }))
  }, [])

  const setCurrentAudio = useCallback(
    (src: string | null, position = 0, audioType: AudioType | null = 'chapter', triggerPlay = false) => {
      setState((prev) => {
        const transitioningFromChapter = prev.audioType === 'chapter' && audioType !== 'chapter'
        const audioChanged = prev.currentAudio !== src
        return {
          ...prev,
          previousAudio: transitioningFromChapter ? prev.currentAudio : prev.previousAudio,
          previousPosition: transitioningFromChapter ? prev.currentPosition : prev.previousPosition,
          currentAudio: src,
          currentPosition: position,
          audioType: audioType,
          audioIsReady: audioChanged ? false : prev.audioIsReady, // Reset ready state when audio changes
          isPlaying: triggerPlay ? true : prev.isPlaying,
          hasInteracted: triggerPlay ? true : prev.hasInteracted,
        }
      })
    },
    []
  )

  const setAudioType = useCallback((audioType: AudioType | null) => {
    setState((prev) => ({ ...prev, audioType }))
  }, [])

  const resumePreviousAudio = useCallback(() => {
    setState((prev) => {
      if (prev.previousAudio) {
        return {
          ...prev,
          currentAudio: prev.previousAudio,
          currentPosition: prev.previousPosition,
          audioType: 'chapter', // Assume previous was a chapter
          previousAudio: null,
          previousPosition: 0,
          isPlaying: true,
        }
      }
      return prev
    })
  }, [])

  const value = useMemo(
    () => ({
      ...state,
      play,
      pause,
      setVolume,
      setMuted,
      toggleMute,
      setCurrentAudio,
      seek,
      resumePreviousAudio,
      setAudioReady,
      setAudioPosition,
      setAudioType,
      setHasInteracted,
    }),
    [
      state,
      play,
      pause,
      setVolume,
      setMuted,
      toggleMute,
      setCurrentAudio,
      seek,
      resumePreviousAudio,
      setAudioReady,
      setAudioPosition,
      setHasInteracted,
      setAudioType,
    ]
  )

  return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>
}

export function useAudio() {
  const context = useContext(AudioContext)
  if (context === undefined) {
    throw new Error('useAudio must be used within a AudioProvider')
  }
  return context
}
